import { AddonSubCategory, OfferSubCategory } from '../../../common/enums.js';
import { isProductSubcategory } from './productSubcategoryUtilsCoCo.js';
import type { ProductOfferingCoco } from '../../../generated/api/productOfferingCoco.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';
import type { ProductSpecRuntimeCharacteristic } from '../../../generated/api/productSpecRuntimeCharacteristic.js';

export enum PlanProductRuntimeCharacteristic {
  PURPOSE_OF_USE = 'purpose_of_use',
  MSISDN = 'MSISDN',
  USER_FIRST_NAME = 'User_First_Name',
  USER_LAST_NAME = 'User_Last_Name',
  EMAIL_ID = 'Email_ID',
  ORDERING_MOBILE_NUMBER = 'Ordering_Mobile_Number',
  LANGUAGE = 'Language',
  FIBER_MODE = 'fiber_mode',
  DIRECTORY_LISTING_PUBLICITY = 'directory_listing_publicity',
}

export enum FiberMode {
  SINGLE_MODE = 'single_mode',
  MULTI_MODE = 'multi_mode',
}

export enum OrderParamExtensions {
  SUBSCRIPTION_COST_CENTER = 'subscriptionCostCenter',
  SUBSCRIPTION_REFERENCE = 'subscriptionReference',
  // delivery related extensions
  DELIVERY_TYPE = 'delivery_type',
}

export enum DeliveryRuntimeCharacteristic {
  ADDRESS_ID = 'addressId',
  RECIPIENT_NAME = 'name',
  PHONE_NUMBER = 'phone_number',
  ADDITIONAL_INFO = 'additional_info',
  STREET_ADDRESS = 'street_address',
  POST_CODE = 'post_code',
  POST_OFFICE = 'post_office',
  COMPANY_NAME = 'company_name',
}

export enum DeviceRuntimeCharacteristic {
  DEVICE_ENROLLMENT = 'deviceEnrollment',
  ENROLLMENT_PROGRAM = 'enrollmentProgram',
  ENROLLMENT_CUSTOMER_ID = 'enrollmentCustomerId',
  ENROLLMENT_ALIAS = 'enrollmentAlias',
}

export enum ProductOfferConfigField {
  MNP = 'MNP',
  NEW_NUMBER = 'NEW_NUMBER',
  NUMBER_PRIVACY = 'NUMBER_PRIVACY', // directory status of number
  ESIM = 'ESIM',
  PSIM = 'PSIM',
  ACTIVE_SIM = 'ACTIVE_SIM',
}

export enum BundleAddonGroupRuntimeCharacteristic {
  ESIM = 'esim',
  PSIM = 'psim',
  SIM_TYPE = 'sim_type',
  ICCID = 'ICCID',
  OWN_SIM_CARD = 'own_SIM_card_',
  APN_NAME = 'apn_name',
  MAC_ADDRESS = 'mac_address',
}

/** Allowed values of the own_SIM_card_ characteristic. */
export enum OwnSimCardValue {
  YES = 'yes',
  NO = 'no',
}

/** sim_type value of a physical SIM, next to 'pSIM' which the catalogue uses. */
export const PHYSICAL_SIM_TYPE = 'physical_sim';

export enum BundleAddonGroupName {
  ELISA_SIM = 'Elisa SIM',
}

export interface CharacteristicsFlagsCoCoOptions {
  selectedProductOffers: ProductOfferingsResponse[];
  basketItemId: string;
}

/** Customer-owned CPE offerings that require the customer to provide a MAC address. */
export const requiresMacAddress = (offering: ProductOfferingCoco): boolean =>
  offering.productOfferingType?.subcategory === AddonSubCategory.CUSTOMER_OWN_CPE &&
  (offering.productSpecRuntimeCharacteristics ?? []).some(
    characteristic => characteristic.name === BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS
  );

export const isCustomerOwnedCpe = (offering: ProductOfferingCoco): boolean =>
  offering.productOfferingType?.subcategory === AddonSubCategory.CUSTOMER_OWN_CPE;

// eSIM and pSIM share the SIM_POT subcategory, so the SIM type is what tells them apart.
export const isPhysicalSimOffering = (offering: ProductOfferingCoco): boolean =>
  offering.name?.value?.toLowerCase() === BundleAddonGroupRuntimeCharacteristic.PSIM ||
  offering.productSpecRuntimeCharacteristics
    ?.find(characteristic => characteristic.name === BundleAddonGroupRuntimeCharacteristic.SIM_TYPE)
    ?.value?.some(value =>
      [PHYSICAL_SIM_TYPE, BundleAddonGroupRuntimeCharacteristic.PSIM].includes(value.value?.toLowerCase() ?? '')
    ) === true;

// Fixed Mobile Broadband bundles its mandatory pSIM directly instead of in an add-on group.
export const isFixedMobileBroadband = (offering: { productOfferingType?: { subcategory?: string } }): boolean =>
  isProductSubcategory(offering.productOfferingType?.subcategory, OfferSubCategory.FIXED_MOBILE_BROADBAND);

export const getAddonOfferings = (offering: ProductOfferingsResponse): ProductOfferingCoco[] => [
  ...(isFixedMobileBroadband(offering) ? (offering.bundleAddonProductOffering ?? []) : []),
  ...(offering.bundleAddonGroup ?? []).flatMap(group => group.productOffering ?? []),
];

// Fixed Mobile Broadband offers the customer's own SIM only when its pSIM declares own_SIM_card_ with "yes" as an
// allowed value; ICCID there is just the identifier. Other products signal it with an ICCID characteristic.
export const supportsExistingSim = (product: ProductOfferingsResponse, psim: ProductOfferingCoco): boolean => {
  if (!isFixedMobileBroadband(product)) {
    return (
      psim.productSpecRuntimeCharacteristics?.some(
        characteristic => characteristic.name === BundleAddonGroupRuntimeCharacteristic.ICCID
      ) ?? false
    );
  }

  return [...(psim.productSpecRuntimeCharacteristics ?? []), ...(psim.productOfferingRuntimeCharacteristic ?? [])].some(
    characteristic =>
      characteristic.name === BundleAddonGroupRuntimeCharacteristic.OWN_SIM_CARD &&
      characteristic.value?.some(allowedValue => allowedValue.value?.toLowerCase() === OwnSimCardValue.YES)
  );
};

export interface CharacteristicsFlagsCoCoResult {
  fieldsToShow: {
    showPurposeOfUse: boolean;
    showCostCenter: boolean;
    showReference: boolean;
    /** When false, no user (contact) information is collected — only the purpose of use. */
    showUserContact?: boolean;
  };
}

export const getRuntimeCharacteristics = (offering?: ProductOfferingsResponse): ProductSpecRuntimeCharacteristic[] => [
  ...(offering?.planProductOffering?.productSpecRuntimeCharacteristics ?? []),
  ...(offering?.productSpecRuntimeCharacteristics ?? []),
];

export const findRuntimeCharacteristic = (
  offering: ProductOfferingsResponse | undefined,
  name: string
): ProductSpecRuntimeCharacteristic | undefined => getRuntimeCharacteristics(offering).find(c => c.name === name);

export const getFieldsToShow = ({
  selectedProductOffers,
  basketItemId,
}: CharacteristicsFlagsCoCoOptions): ProductOfferConfigField[] => {
  const offering = selectedProductOffers.find(offer => offer.id === basketItemId);
  if (!offering) {
    return [];
  }

  const addonFields = getAddonOfferings(offering).flatMap(productOffering => {
    const name = productOffering.name?.value?.toLowerCase();
    if (name === BundleAddonGroupRuntimeCharacteristic.ESIM) {
      return [ProductOfferConfigField.ESIM];
    }
    if (isPhysicalSimOffering(productOffering)) {
      return supportsExistingSim(offering, productOffering)
        ? [ProductOfferConfigField.PSIM, ProductOfferConfigField.ACTIVE_SIM]
        : [ProductOfferConfigField.PSIM];
    }
    return [];
  });

  const hasSimAddon = addonFields.some(f => f === ProductOfferConfigField.ESIM || f === ProductOfferConfigField.PSIM);

  const hasMsisdnCharacteristic =
    offering.planProductOffering?.productSpecRuntimeCharacteristics?.some(
      c => c.name === PlanProductRuntimeCharacteristic.MSISDN
    ) ?? false;
  // Fixed Mobile Broadband has a pSIM but no phone number selection in checkout.
  const showPhoneNumber = hasMsisdnCharacteristic && hasSimAddon && !isFixedMobileBroadband(offering);

  return [...(showPhoneNumber ? [ProductOfferConfigField.NEW_NUMBER] : []), ...addonFields];
};
